import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, HeadphonesIcon, Package, ChevronRight, MessageCircle, Cpu, TrendingUp, LifeBuoy, Award } from "lucide-react";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import QuoteDialog from "@/components/QuoteDialog";
import AnimatedCounter from "@/components/AnimatedCounter";
import { useSEO } from "@/lib/useSEO";
import { imgProps } from "@/lib/images";

const IMG_SFO = "https://customer-assets-jt897jd0.emergentagent.net/job_gms-precision-hub/artifacts/2h3ygxll_Four%20color%20Sheetfed%20Offset%20Printing%20Machines-2.jpeg";
const IMG_HYBRID = "https://customer-assets-jt897jd0.emergentagent.net/job_gms-precision-hub/artifacts/qb303prq_Hybrid%20Printing%20Machine%20-1.webp";
const IMG_460TF = "https://customer-assets-jt897jd0.emergentagent.net/job_gms-precision-hub/artifacts/nwcmslzp_full%20automatic%20460TF-8.webp";
const IMG_DC700 = "https://customer-assets-jt897jd0.emergentagent.net/job_gms-precision-hub/artifacts/fe07zkoo_GMS%20DC700%20-12.webp";
const WHATSAPP_HREF = `https://wa.me/919810012256?text=${encodeURIComponent("Hello GMS, I would like to discuss my requirement.")}`;

const INDUSTRIES = [
  { slug: "commercial-printing", name: "Commercial Printing", desc: "Magazines, catalogues, books and marketing collateral.", image: IMG_SFO, imageFit: "contain" },
  { slug: "packaging", name: "Packaging", desc: "Folding cartons, flexible and rigid packaging.", image: IMG_460TF, imageFit: "contain" },
  { slug: "labels", name: "Label Manufacturing", desc: "Self adhesive labels, wet glue labels and shrink sleeves.", image: IMG_HYBRID, imageFit: "contain" },
  { slug: "non-woven-bag", name: "Non Woven Bag Manufacturing", desc: "Shopping, gift, drawstring and allied non woven products.", image: IMG_DC700, imageFit: "contain" },
];

const PILLARS = [
  { icon: Cpu, title: "Engineering & Automation", desc: "OEM grade engineering combined with PLC, servo and process automation to modernise your production floor." },
  { icon: TrendingUp, title: "Productivity & ROI", desc: "Recommendations benchmarked on production speed, output consistency and payback, not just specifications." },
  { icon: LifeBuoy, title: "Lifecycle Support", desc: "Installation, operator training, preventive maintenance and spare parts across the full equipment lifecycle." },
  { icon: Award, title: "Technology Consulting", desc: "Capacity planning, greenfield project consulting and technology advisory by senior engineers." },
];

const SERVICES = [
  { icon: Wrench, title: "Engineering & Installation", desc: "Commissioning, foundation planning and rigging support as per the applicable agreement." },
  { icon: HeadphonesIcon, title: "Technical Support", desc: "Remote and onsite technical support to resolve production issues quickly." },
  { icon: ShieldCheck, title: "Preventive Maintenance", desc: "Planned maintenance programmes designed for consistent uptime and long term performance." },
  { icon: Package, title: "Spare Parts", desc: "OEM spare parts support and stocking guidance to reduce downtime." },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [expertOpen, setExpertOpen] = useState(false);

  useSEO({
    title: "GMS | Printing, Packaging & Automation Solutions",
    description: "GMS provides printing, packaging and automation machinery with expert engineering, installation and lifecycle support across India.",
    canonical: "https://graphicmachinerysolutions.com/",
  });

  useEffect(() => {
    api.get("/products", { params: { featured: true, limit: 6 } }).then((r) => setFeatured(r.data)).catch(()=>{});
  }, []);

  return (
    <>
      {/* HERO — refined: text-forward, generous whitespace, subtle geometric accent */}
      <section className="relative border-b border-[#E2E8F0] overflow-hidden" data-testid="hero-section">
        {/* Subtle grid accent, non-distracting */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.06]"
             style={{backgroundImage: "linear-gradient(to right, #0F3D66 1px, transparent 1px), linear-gradient(to bottom, #0F3D66 1px, transparent 1px)", backgroundSize: "80px 80px"}}/>
        <div className="pointer-events-none absolute -right-40 -top-32 w-[520px] h-[520px] rounded-full bg-[#007BFF]/5"/>
        <div className="gms-container relative pt-10 md:pt-14 lg:pt-16 pb-12 md:pb-14 lg:pb-16 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-10 xl:col-span-9 flex flex-col gms-fade-up">
            <div className="gms-eyebrow mb-4">Industrial Technology Partner</div>
            <h1 className="font-heading font-extrabold text-[#0F3D66] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-5xl">
              Engineering Better <span className="text-[#007BFF]">Productivity</span> for India's Print &amp; Packaging Leaders
            </h1>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-[#5F6B76] leading-relaxed max-w-3xl">
              GMS helps businesses identify, implement and support the right printing, packaging and automation technology. We combine engineering experience, global technology access and long term partnership to deliver measurable business outcomes.
            </p>
            <div className="mt-6 md:mt-7 flex flex-wrap gap-3">
              <button onClick={() => setQuoteOpen(true)} className="gms-btn-primary" data-testid="hero-quote-btn">Request a Quote <ArrowRight className="w-4 h-4" strokeWidth={2} /></button>
              <button onClick={() => setExpertOpen(true)} className="gms-btn-secondary" data-testid="hero-expert-btn">Talk to an Expert</button>
              <a href={WHATSAPP_HREF} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white px-5 py-3 text-sm font-semibold rounded-sm transition-colors" data-testid="hero-whatsapp-btn"><MessageCircle className="w-4 h-4" strokeWidth={2}/> WhatsApp Now</a>
            </div>
            <div className="mt-8 md:mt-10 grid grid-cols-3 max-w-3xl gap-4 sm:gap-8 lg:gap-12" data-testid="hero-stats">
              {[
                { v: 30, l: "Years of Industry Experience", t: "stat-years" },
                { v: 500, l: "Successful Installations", t: "stat-installations" },
                { v: 300, l: "Satisfied Customers", t: "stat-customers" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-[#007BFF] pl-3 sm:pl-5">
                  <AnimatedCounter value={s.v} suffix="+" testid={s.t} className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F3D66] tracking-tight block" />
                  <div className="text-[10px] sm:text-[11px] uppercase tracking-widest text-[#5F6B76] mt-2 leading-tight">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="gms-section" data-testid="pillars-section">
        <div className="gms-container">
          <div className="grid lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="gms-eyebrow mb-3">Why GMS</div>
              <h2 className="font-heading font-bold text-[#0F3D66] text-4xl lg:text-5xl leading-tight tracking-tight">An industrial technology platform, not a catalogue.</h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 text-[#5F6B76] text-lg leading-relaxed">
              GMS combines machinery, automation and consulting into a single partnership that measurably improves production speed, output consistency and equipment uptime.
              <Link to="/solutions" className="mt-4 flex items-center gap-1 text-sm font-semibold text-[#007BFF]" data-testid="home-solutions-link">Explore solutions <ArrowRight className="w-4 h-4" strokeWidth={2} /></Link>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PILLARS.map((p) => (
              <div key={p.title} className="p-8 border border-[#E2E8F0] bg-white hover:border-[#007BFF] hover:-translate-y-1" style={{transition:"border-color 250ms, transform 250ms"}} data-testid={`pillar-${p.title}`}>
                <p.icon className="w-7 h-7 text-[#007BFF] mb-5" strokeWidth={1.5}/>
                <h3 className="font-heading font-bold text-[#0F3D66] text-xl leading-snug">{p.title}</h3>
                <p className="text-sm text-[#5F6B76] mt-3 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MACHINE FINDER STRIP */}
      <section className="gms-section bg-[#F5F7FA]" data-testid="machine-finder-strip">
        <div className="gms-container">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
            <div className="lg:col-span-8">
              <div className="gms-eyebrow mb-3">Intelligent recommendation</div>
              <h2 className="font-heading font-bold text-[#0F3D66] text-4xl lg:text-5xl leading-tight tracking-tight">Configure your capacity plan.<br/>Get matched to the right technology.</h2>
            </div>
            <div className="lg:col-span-4 text-[#5F6B76] text-lg leading-relaxed">
              Our engineers assess industry, application and business objectives to shortlist the machines that deliver measurable performance.
            </div>
          </div>
          <Link to="/machine-finder" className="gms-btn-primary" data-testid="home-machine-finder-cta">Launch Machine Finder <ArrowRight className="w-4 h-4" strokeWidth={2} /></Link>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="gms-section" data-testid="industries-section">
        <div className="gms-container">
          <div className="mb-12">
            <div className="gms-eyebrow mb-3">Industries we serve</div>
            <h2 className="font-heading font-bold text-[#0F3D66] text-4xl lg:text-5xl tracking-tight">Precision for every sector.</h2>
            <Link to="/industries" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#007BFF]" data-testid="home-industries-link">All industries <ArrowRight className="w-4 h-4" strokeWidth={2} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {INDUSTRIES.map((i) => (
              <Link key={i.slug} to={`/products?industry=${i.slug}`} className="gms-card p-6 group flex flex-col" data-testid={`industry-card-${i.slug}`}>
                <div className="aspect-[3/2] bg-[#F5F7FA] mb-5 overflow-hidden flex items-center justify-center">
                  <img
                    {...imgProps(i.image, "(min-width: 1024px) 25vw, (min-width: 768px) 50vw, 100vw")}
                    alt={`GMS printing and packaging machine for ${i.name.toLowerCase()}`}
                    loading="lazy"
                    decoding="async"
                    className={`w-full h-full ${i.imageFit === "contain" ? "object-contain p-2" : "object-cover"} group-hover:scale-105`}
                    style={{transition:"transform 400ms", imageRendering: "auto"}}
                  />
                </div>
                <h3 className="font-heading font-bold text-[#0F3D66] text-xl">{i.name}</h3>
                <p className="text-sm text-[#5F6B76] mt-2 flex-1">{i.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#007BFF]">Explore <ChevronRight className="w-4 h-4" strokeWidth={2} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="gms-section bg-[#F5F7FA]" data-testid="featured-products">
        <div className="gms-container">
          <div className="flex items-end justify-between mb-12">
            <div>
              <div className="gms-eyebrow mb-3">Featured machinery</div>
              <h2 className="font-heading font-bold text-[#0F3D66] text-4xl lg:text-5xl tracking-tight">Production ready technology.</h2>
            </div>
            <Link to="/products" className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-[#007BFF]" data-testid="view-all-products">View all <ArrowRight className="w-4 h-4" strokeWidth={2} /></Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <ProductCard key={p.slug} product={p} />)}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="gms-section bg-[#0F3D66] text-white" data-testid="services-section">
        <div className="gms-container">
          <div className="grid lg:grid-cols-12 gap-10 mb-14 items-end">
            <div className="lg:col-span-7">
              <div className="text-xs uppercase tracking-widest text-[#007BFF] font-bold mb-3">Engineering services</div>
              <h2 className="font-heading font-bold text-white text-4xl lg:text-5xl tracking-tight">More than machines. A long term partnership.</h2>
            </div>
            <div className="lg:col-span-5 text-white/80 text-lg leading-relaxed">
              Every installation is backed by GMS engineering, from foundation planning to lifecycle support and predictive maintenance.
              <Link to="/services" className="mt-4 flex items-center gap-1 text-sm font-semibold text-white hover:text-[#007BFF]" data-testid="home-services-link">View all services <ArrowRight className="w-4 h-4" strokeWidth={2} /></Link>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SERVICES.map((s) => (
              <div key={s.title} className="p-6 border border-white/15 hover:border-white/40 transition-colors" data-testid={`service-${s.title}`}>
                <s.icon className="w-6 h-6 text-[#007BFF] mb-4" strokeWidth={1.75} />
                <h3 className="font-heading font-bold text-lg text-white">{s.title}</h3>
                <p className="text-sm text-white/70 mt-2 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BAND */}
      <section className="gms-section">
        <div className="gms-container">
          <div className="border border-[#E2E8F0] bg-[#F5F7FA] p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <div className="gms-eyebrow mb-3">Ready to upgrade?</div>
              <h2 className="font-heading font-bold text-[#0F3D66] text-3xl lg:text-4xl tracking-tight">Talk to a GMS sales engineer today.</h2>
              <p className="text-[#5F6B76] mt-3 max-w-2xl">A one hour discovery call, a personalised machine shortlist and clear commercial guidance.</p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button onClick={() => setQuoteOpen(true)} className="gms-btn-primary" data-testid="cta-quote">Request Quote</button>
              <button onClick={() => setExpertOpen(true)} className="gms-btn-secondary" data-testid="cta-expert">Talk to Expert</button>
            </div>
          </div>
        </div>
      </section>

      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} leadType="quote" />
      <QuoteDialog open={expertOpen} onOpenChange={setExpertOpen} leadType="expert" />
    </>
  );
}
